var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { action, observable, toJS, computed } from "mobx";
import { Sport, EventsBySportIdRequest } from "sb-bff-integration";
import { deepMerge } from "sb-helpers";
import { FilterPredicate, SubFilterPredicate, FilterFunctions, EventsRequestType, ActiveRequestsType, RecursiveRequestFunctions } from "../typings";
import Debounce from "debounce-decorator";
import { bffRequestBuilder, queryBuilder, DateRangeMapper, LoadedData, isDifferentResponse, uniqueElementsById, isEntityFromType, compareArrays } from "../helpers";
import { EntityType } from "sbtech-sports-data-api-contracts";
var Retriever = /** @class */ (function () {
    function Retriever(bff, sdapi, dataProvider, deviceChangeDispatcherMock) {
        this.FullLoadEventsCount = 100;
        this.ReloadTimeout = 60000;
        this.bff = bff;
        this.sdapi = sdapi;
        this.dataProvider = dataProvider;
        this.activeRequests = this.initializeRequests();
        this.deviceChangeDispatcher = deviceChangeDispatcherMock;
        this.data = { events: observable([]), markets: observable([]), leagues: observable([]) };
        this.temporaryData = { events: [], markets: [], leagues: [] };
        this.loadedEvents = 0;
        this.reloadTimeout = -1;
        this.subLoaders = this.initializeLoaders();
        this.regularUpdate = this.regularUpdate.bind(this);
        this.regularUpdateTimeOut = this.regularUpdateTimeOut.bind(this);
        this.processError = this.processError.bind(this);
        this.applyAddref = this.applyAdd.bind(this);
        this.sub = undefined;
        this.filterPredicate = this.dataProvider.filterPredicate;
        this.subFilterPredicate = SubFilterPredicate.None;
        this.changesCache = { events: [], markets: [] };
        this.addsCache = { events: [], markets: [] };
        this.loadedData = new LoadedData();
        this.currentSports = [];
    }
    Retriever.prototype.activate = function () {
        var _this = this;
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.setLoader(true, type);
        });
        window.clearTimeout(this.reloadTimeout);
        this.clearData();
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.activate();
    };
    Retriever.prototype.deactivate = function () {
        var _this = this;
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.setLoader(false, type);
        });
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.subLoaders[type].isFirstLoad = true;
        });
        window.clearTimeout(this.reloadTimeout);
        this.clearData();
        this.currentSports = [];
        this.sub && this.sub.unsubscribe();
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.deactivate();
        this.filterPredicate = this.dataProvider.filterPredicate;
    };
    Retriever.prototype.setParams = function (sportIds) {
        var _this = this;
        Object.keys(this.activeRequests).forEach(function (type) {
            bffRequestBuilder.buildIds(_this.activeRequests[type].request, sportIds);
        });
        this.currentSports = sportIds;
    };
    /**
     * Update query filter to be for specific sport ids
     * @param {string[]} sportIds Newly selected sport ids
     */
    Retriever.prototype.querySportUpdate = function (sportIds) {
        var _this = this;
        this.currentSports = sportIds;
        var shouldUpdateFirstLoad = this.loadedData.updateSports(sportIds);
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (_this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.setActiveRequest(type);
            var bffRequestParams = _this.getBFFRequestParams(type);
            bffRequestBuilder.buildSportIdsUpdate(bffRequestParams, sportIds);
        });
        this.filterPredicate = FilterPredicate.Sport;
        this.loadData();
    };
    /**
     * Update query filter to be for specific date range
     * @param {DateRangeProp} dateRange Newly selected date range
     */
    Retriever.prototype.queryDatesUpdate = function (dateRange, sportIds) {
        var _this = this;
        var shouldUpdateFirstLoad = this.loadedData.updateDates(sportIds, dateRange.from || new Date());
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (_this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.setActiveRequest(type);
            var bffRequestParams = _this.getBFFRequestParams(type);
            bffRequestBuilder.buildDateRangeUpdate(bffRequestParams, dateRange);
        });
        this.filterPredicate = FilterPredicate.Date;
        this.loadData();
    };
    /**
     * Update query filter to be for specific league ids from sport ids
     * @param {string[]} leagueIds Newly selected league ids
     * @param {string[]} sportIds Newly selected sport ids
     */
    Retriever.prototype.queryLeaguesUpdate = function (leagueIds, sportIds) {
        var _this = this;
        this.currentSports = sportIds;
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.subLoaders[type].isFullLoadFinished = false;
            _this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.setActiveRequest(type);
            var bffRequestParams = _this.getBFFRequestParams(type);
            bffRequestBuilder.buildLeagueIdsUpdate(bffRequestParams, leagueIds, sportIds);
        });
        this.filterPredicate = FilterPredicate.Sport;
        this.loadData();
    };
    /**
     *
     * @param {string[]} regionId
     */
    Retriever.prototype.queryRegionIdUpdate = function (regionId, sportId) {
        var _this = this;
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.subLoaders[type].isFullLoadFinished = false;
            _this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.setActiveRequest(type);
            var bffRequestParams = _this.getBFFRequestParams(type);
            var pagination = _this.recalculatePagination({}, false);
            bffRequestBuilder.buildRegionIdUpdate(bffRequestParams, regionId, sportId, pagination);
        });
        this.filterPredicate = FilterPredicate.Sport;
        this.loadData();
    };
    /**
     * Update query filter to be for current tag
     * @param {string} currentTag Tag to be changed
     */
    Retriever.prototype.queryTagsUpdate = function (currentTag) {
        var _this = this;
        var shouldUpdateFirstLoad = this.loadedData.updateTags(currentTag);
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (_this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.setActiveRequest(type);
            var bffRequestParams = _this.getBFFRequestParams(type);
            bffRequestBuilder.buildEventTagsUpdate(bffRequestParams, [currentTag]);
        });
        this.filterPredicate = FilterPredicate[currentTag.toString()];
        this.loadData();
    };
    /**
     * Update query filter to be for favourites
     * @param {string[]} eventIds Ids of favourite events
     */
    Retriever.prototype.queryFavoritesUpdate = function (favoriteEvents) {
        var _this = this;
        this.currentSports = favoriteEvents.map(function (e) { return e.sportId; });
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.subLoaders[type].isFullLoadFinished = false;
            _this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(function (type) {
            _this.setActiveRequest(type, false);
            var bffRequestParams = _this.getBFFRequestParams(type);
            bffRequestBuilder.buildEventIdsUpdate(bffRequestParams, favoriteEvents);
        });
        this.filterPredicate = FilterPredicate.Favorites;
        this.loadData();
    };
    /**
     * Execute according request for fetching data on every x milliseconds - ReloadTimeout
     */
    Retriever.prototype.loadData = function () {
        var _this = this;
        if (this.isRequestAvailable()) {
            window.clearTimeout(this.reloadTimeout);
            this.sub && this.sub.unsubscribe();
            Object.keys(this.activeRequests).forEach(function (type) {
                _this["loadData" + _this.activeRequests[type].type](type);
            });
            this.reloadTimeout = window.setTimeout(function () { return _this.loadData(); }, this.ReloadTimeout);
        }
    };
    /**
     * Process received data and query recursively if needed
     * @param {EventsResponse} response
     * @param {Pagination} pagination
     */
    Retriever.prototype.loadDataSuccessCb = function (response, pagination, requestType) {
        if (response.events && response.events.length) {
            this.subLoaders[requestType].isFirstLoad ? this.loadNewData(response) : this.updateTemporaryData(response);
            if (response.events.length === pagination.top && this.loadedEvents < this.dataProvider.maxEventsToLoad) {
                return this.recalculatePagination(pagination);
            }
        }
        return false;
    };
    /**
     * Calculate request pagination despite the received amount of data
     * @param {Pagination} pagination Current pagination of the request. Empty by default.
     */
    Retriever.prototype.recalculatePagination = function (pagination, shouldRecalcSkip) {
        if (shouldRecalcSkip === void 0) { shouldRecalcSkip = true; }
        var fullLoad = Math.min(this.dataProvider.maxEventsToLoad, this.FullLoadEventsCount);
        var loadedEvents = (pagination.top || 0) + (pagination.skip || 0);
        return {
            top: Math.min(this.dataProvider.maxEventsToLoad - this.loadedEvents, fullLoad),
            skip: shouldRecalcSkip ? loadedEvents : 0
        };
    };
    /**
     * Inital received data processing
     * @param {EventsResponse} response
     */
    Retriever.prototype.loadNewData = function (response) {
        this.add({ events: response.events, markets: response.markets });
        this.replaceLeagues({ leagues: response.leagues });
    };
    /**
    * renew Data on JWTToken change
    */
    Retriever.prototype.renewJWTToken = function () {
        this.loadData();
    };
    /**
    * sets the customers regionId
    * @param {regionId}  customers regionId
    */
    Retriever.prototype.setRegionId = function (regionId) {
        var _this = this;
        Object.keys(this.activeRequests).forEach(function (type) {
            bffRequestBuilder.buildRegionIds(_this.activeRequests[type].request, _this.dataProvider.ipCountry.concat(regionId));
        });
    };
    /**
    * removes the customers regionId
    *
    */
    Retriever.prototype.removeRegionId = function () {
        var _this = this;
        Object.keys(this.activeRequests).forEach(function (type) {
            bffRequestBuilder.buildRegionIds(_this.activeRequests[type].request, _this.dataProvider.ipCountry);
        });
    };
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by top leagues)
     * @param {EventsResponse} response
     */
    Retriever.prototype.buildUpdateQueryByTopLeagues = function (response, requestType) {
        var queryParams = this.getQueryParams(requestType);
        this.query = queryBuilder.buildQueryTopLeagues(queryParams);
        this.updateData(queryParams.request, response, requestType);
    };
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    Retriever.prototype.buildUpdateQueryByRegionId = function (response, requestType) {
        var queryParams = this.getQueryParams(requestType);
        this.query = queryBuilder.buildQueryByRegionId(queryParams);
        this.updateData(queryParams.request, response, requestType);
    };
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    Retriever.prototype.buildUpdateQueryByEventIds = function (response, requestType) {
        var queryParams = this.getQueryParams(requestType);
        this.query = queryBuilder.buildQueryByEventIds(queryParams);
        this.updateData(queryParams.request, response, requestType);
    };
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    Retriever.prototype.buildUpdateQueryByLeagueIds = function (response, requestType) {
        var queryParams = this.getQueryParams(requestType);
        this.query = queryBuilder.buildQueryByLeagueIds(queryParams);
        this.updateData(queryParams.request, response, requestType);
    };
    /**
     * Subscribe for updates from SportData API
     */
    Retriever.prototype.querySub = function () {
        this.sub = this.sdapi.subscribeForEvents(this.query);
    };
    /**
     * Handler for update events - execute add,change or remove methods
     * @param {EventPushResponse} x
     */
    Retriever.prototype.regularUpdate = function (x) {
        var _this = this;
        Object.keys(x.data).forEach(function (prop) {
            var property = prop === "add" ? "addChanges" : prop;
            _this[property](x.data[prop]);
        });
    };
    /**
     * Reset observable data
     */
    Retriever.prototype.clearData = function () {
        this.data.events.replace([]);
        this.data.markets.replace([]);
        this.data.leagues.replace([]);
        this.temporaryData = { events: [], markets: [], leagues: [] };
        this.loadedData.clearData();
    };
    // VERY IMPORTANT:
    // SDAPI is sending one event per change
    // 20 events - 20 time triggering the update logic and render
    // batching them and executing every half a second reduces
    // CPU usage to almost none
    /**
     * Handler for "Change" event from SportData API
     * @param {EventPayload} x
     */
    Retriever.prototype.change = function (x) {
        var _this = this;
        Object.keys(this.changesCache).map(function (prop) {
            var _a;
            return (_a = _this.changesCache[prop]).splice.apply(_a, __spread([_this.changesCache[prop].length, 0], x[prop]));
        });
        this.applyChanges();
    };
    Retriever.prototype.applyChanges = function () {
        this.batchedChange(this.changesCache);
        this.changesCache = { events: [], markets: [] };
    };
    Retriever.prototype.replaceLeagues = function (x) {
        this.data.leagues.replace(uniqueElementsById(x.leagues.concat(this.data.leagues.slice())));
    };
    /**
     * Add and update currently stored data
     * @param {EventsResponse} x
     * @param {Function} filterFn Filter predicate
     */
    Retriever.prototype.updateCurrentData = function (x, filterFn, requestType) {
        var _this = this;
        var collections = FilterFunctions[this.filterPredicate].collections;
        Object.keys(this.data).forEach(function (collection) {
            var currentData = _this.data[collection].slice();
            var dataToRemain = collections.indexOf(collection) > -1
                ? currentData.filter(function (e) { return !filterFn(e) || !isEntityFromType(e, requestType, _this.data.events); })
                : currentData;
            _this.data[collection].replace(uniqueElementsById(dataToRemain.concat(x[collection])));
        });
    };
    /**
     * Handler for 'Add' update event from SportData API
     * @param {EventPayload} x
     */
    Retriever.prototype.add = function (x) {
        var _this = this;
        Object.keys(x).forEach(function (prop) { return _this.data[prop].push.apply(_this.data[prop], x[prop]); });
    };
    Retriever.prototype.addChanges = function (x) {
        var _this = this;
        Object.keys(this.addsCache).forEach(function (prop) { return _this.addsCache[prop].concat(x[prop]); });
        if (this.addsCache.events.length > 0 || this.addsCache.markets.length > 0) {
            this.applyAddref(this.addsCache);
        }
    };
    Retriever.prototype.applyAdd = function () {
        this.batchedAdd(this.addsCache);
        this.addsCache = { events: [], markets: [] };
    };
    Retriever.prototype.batchedAdd = function (x) {
        var _this = this;
        var tempObject = __assign({}, x);
        Object.keys(this.data).forEach(function (prop) {
            if (x[prop]) {
                tempObject[prop] = _this.data[prop].concat(x[prop].slice());
                _this.data[prop].replace(tempObject[prop]);
            }
            else {
                tempObject[prop] = _this.data[prop].slice();
            }
        });
    };
    /**
     * Handler for 'Remove' update event from SportData API
     * @param {EventPayload} x
     */
    Retriever.prototype.remove = function (x) {
        var _this = this;
        Object.keys(x).forEach(function (prop) {
            x[prop].forEach(function (y) {
                var i = _this.data[prop].findIndex(function (z) { return z.id === y; });
                if (i > -1) {
                    _this.data[prop].splice(i, 1);
                }
            });
        });
    };
    Retriever.prototype.batchedChange = function (x) {
        var _this = this;
        Object.keys(x).forEach(function (prop) {
            x[prop].forEach(function (y) {
                var i = _this.data[prop].findIndex(function (z) { return z.id === y.id; });
                if (i > -1) {
                    var item = deepMerge(toJS(_this.data[prop][i]), y);
                    if (prop === "markets" && y.selections && y.selections.length) {
                        item.selections = y.selections;
                    }
                    _this.data[prop][i] = item;
                }
            });
        });
    };
    /**
     * Get current filter function
     * @param {EventListPresenterRequest} request
     */
    Retriever.prototype.getFilterFunction = function (request, requestType) {
        var ids = this.getFilterIds(request, requestType);
        var from, to;
        if (request.dateRange) {
            this.dataProvider.useNewTimeOnEveryRequest && (request.dateRange.from = new Date());
            from = request.dateRange.from;
            to = DateRangeMapper[request.dateRange.timeRange](from);
        }
        return this.getCurrentFilter.bind(this, ids, from, to);
    };
    /**
     * Bind the selected filter function parameters
     * @param {string[]} sportIds
     * @param {Date} from
     * @param {Date} to
     * @param {Entity} x
     */
    Retriever.prototype.getCurrentFilter = function (sportIds, from, to, x) {
        return this[FilterFunctions[this.filterPredicate].fn](x, sportIds, from, to);
    };
    /**
     * Check if entity is from selected sportIds
     * @param {Entity} x
     * @param {string[]} sportIds
     */
    Retriever.prototype.filterBySport = function (x, sportIds) {
        return sportIds.indexOf(x.sportId) > -1 && this.filterBySubPredicate(x);
    };
    ;
    /**
     * Check if event has liveStream
     * @param {Event} x
     */
    Retriever.prototype.filterByLiveStream = function (x) {
        return this.filterByTag(x, EventsBySportIdRequest.EventTagsEnum.LiveStreaming.toString()) && this.filterBySubPredicate(x);
    };
    /**
     * Check if event has specific tag
     * @param {Event} x
     * @param {String} tag
     */
    Retriever.prototype.filterByTag = function (x, tag) {
        return x.tags.indexOf(tag) > -1;
    };
    /**
     * Check if Event or Market is in this date/time range
     * @param {Event|Market} x
     * @param {string[]} sportIds
     * @param {Date} from
     * @param {Date} to
     */
    Retriever.prototype.filterBySportAndDate = function (x, sportIds, from, to) {
        var eventDateTime = new Date(x.startEventDate || x.startDate);
        return this.filterBySport(x, sportIds) && (eventDateTime >= from && eventDateTime < to);
    };
    /**
     * Check if event is in favourites
     * @param {Event} x
     * @param {string[]} eventIds
     */
    Retriever.prototype.filterByFavorites = function (x, eventIds) {
        return eventIds.indexOf(x.id) > -1 && this.filterBySubPredicate(x);
    };
    /**
     * Request events by top leagues and sport ids
     */
    Retriever.prototype.loadDataByTopLeagues = function (requestType) {
        var _this = this;
        var activeRequest = __assign({}, this.activeRequests[requestType].request);
        var loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.bff.getEventsFromTopLeaguesBySportId(activeRequest)
            .then(function (response) {
            if (_this.isValidResponse(activeRequest, response, requestType)) {
                return;
            }
            _this.buildUpdateQuery(activeRequest, response, requestType);
            if (response.events && response.events.length < _this.dataProvider.maxEventsToLoad &&
                (response.events.length === 0 || !_this.dataProvider.isTopLeaguesOnly)) {
                _this.loadedEvents = response.events.length;
                var nextPagination = _this.recalculatePagination({ top: response.events.length, skip: 0 }, false);
                var request = bffRequestBuilder.buildSportIdRequest(activeRequest, nextPagination);
                _this.loadDataBySportId(request, requestType);
            }
            else {
                loader.isLoading = false;
                _this.updateWithTemporaryData(activeRequest, requestType);
            }
        }).catch(this.processError);
    };
    /**
     * Request events by sport ids
     * @param {EventsBySportIdRequest} request Request for events from regular league ids
     */
    Retriever.prototype.loadDataBySportId = function (request, requestType) {
        var _this = this;
        this.bff.getEventsBySportId(request)
            .then(function (response) {
            if (_this.isValidResponse(request, response, requestType)) {
                return;
            }
            _this.processRecursiveResponse(request, response, requestType, RecursiveRequestFunctions.BySportId);
        }).catch(this.processError);
    };
    Retriever.prototype.updateWithTemporaryData = function (request, requestType) {
        if (!this.subLoaders[requestType].isFirstLoad) {
            var filterFn = this.getFilterFunction(request, requestType);
            this.temporaryData.leagues = uniqueElementsById(this.temporaryData.leagues);
            this.updateCurrentData(this.temporaryData, filterFn, requestType);
        }
        this.subLoaders[requestType].isFullLoadFinished = true;
        this.subLoaders[requestType].isFirstLoad = false;
        if (!this.loader.isFirstLoad && this.loader.isFullLoadFinished) {
            this.temporaryData = { events: [], markets: [], leagues: [] };
        }
    };
    /**
     * Request events by event ids
     */
    Retriever.prototype.loadDataByEventIds = function (requestType) {
        var _this = this;
        var activeRequest = this.activeRequests[requestType].request;
        var loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.bff.getEventsByEventId(activeRequest)
            .then(function (response) {
            _this.buildUpdateQuery(activeRequest, response, requestType);
            _this.updateWithTemporaryData(activeRequest, requestType);
            _this.setLoader(false, requestType);
        }).catch(this.processError);
    };
    /**
     * Request events by country
     */
    Retriever.prototype.loadDataByRegionId = function (requestType) {
        var _this = this;
        var activeRequest = this.activeRequests[requestType].request;
        var loader = this.subLoaders[requestType];
        this.loadedEvents = 0;
        bffRequestBuilder.buildPagination(activeRequest, this.recalculatePagination({}, false));
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.bff.getEventsByRegionId(activeRequest)
            .then(function (response) {
            _this.buildUpdateQuery(activeRequest, response, requestType);
            _this.loadedEvents = response.events ? response.events.length : 0;
            var nextPagination = _this.recalculatePagination(activeRequest.pagination);
            if (nextPagination) {
                bffRequestBuilder.buildPagination(activeRequest, nextPagination);
                _this.loadDataByRegionIdAndPagination(activeRequest, requestType);
            }
            else {
                loader.isLoading = false;
                _this.updateWithTemporaryData(activeRequest, requestType);
            }
        }).catch(this.processError);
    };
    Retriever.prototype.loadDataByRegionIdAndPagination = function (request, requestType) {
        var _this = this;
        this.bff.getEventsByRegionId(request)
            .then(function (response) {
            _this.processRecursiveResponse(request, response, requestType, RecursiveRequestFunctions.ByRegionId);
        }).catch(this.processError);
    };
    Retriever.prototype.processRecursiveResponse = function (request, response, requestType, nextRequestMethod) {
        var loader = this.subLoaders[requestType];
        this.loadedEvents += response.events ? response.events.length : 0;
        var nextPagination = this.loadDataSuccessCb(response, request.pagination || {}, requestType);
        loader.isLoading = false;
        if (nextPagination) {
            bffRequestBuilder.buildPagination(request, nextPagination);
            this[nextRequestMethod] && this[nextRequestMethod](request, requestType);
        }
        else {
            this.updateWithTemporaryData(request, requestType);
        }
    };
    /**
     * Request events by league ids
     */
    Retriever.prototype.loadDataByLeagueIds = function (requestType) {
        var _this = this;
        var activeRequest = this.activeRequests[requestType];
        var loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.bff.getEventsByLeagueId(activeRequest.request)
            .then(function (response) {
            _this.buildUpdateQuery(activeRequest, response, requestType);
            _this.updateWithTemporaryData(activeRequest.request, requestType);
            _this.setLoader(false, requestType);
        }).catch(this.processError);
    };
    /**
     * Sets loader props
     * @param {boolean} isRequestInProcess Is request in process
     */
    Retriever.prototype.setLoader = function (isRequestInProcess, type) {
        this.subLoaders[type].isLoading = isRequestInProcess;
        this.subLoaders[type].isFullLoadFinished = !isRequestInProcess;
    };
    /**
     * Process error from bff request
     */
    Retriever.prototype.processError = function (error) {
        var _this = this;
        Object.keys(this.subLoaders).forEach(function (type) {
            _this.setLoader(false, type);
        });
        // TODO: Process error
        console["error"](error);
    };
    /**
     * Update current data and subscribe for its updates
     * @param {EventListPresenterRequest} request Current request
     * @param {EventsResponse} response Received response
     */
    //It never receives requestType = "outrights" as we are not subscribing fot outright updates
    Retriever.prototype.updateData = function (request, response, requestType) {
        // Get the current filtering function and pass in
        this.updateRetrieverData(request, response, requestType);
        // Need to understand how we are going to pull more than 100 items
        // Probably going to be solved on the BFF
        // sub to query only after it arrives
        // missing some of the changes that happened during the pull
        // SDAPI team is working on improving performance of the pull
        this.querySub();
    };
    Retriever.prototype.updateTemporaryData = function (response) {
        var _this = this;
        Object.keys(this.temporaryData).forEach(function (collection) {
            _this.temporaryData[collection] = _this.temporaryData[collection].concat(response[collection]);
        });
    };
    Retriever.prototype.filterBySubPredicate = function (x) {
        if (this.subFilterPredicate === SubFilterPredicate.None ||
            (x.entityType !== EntityType.Event && x.entityType.toString() !== Sport.EntityTypeEnum.Event.toString())) {
            return true;
        }
        return this.filterByTag(x, SubFilterPredicate[this.subFilterPredicate]);
    };
    /**
     * Set active BFF request to request by event ids
     * @param {boolean} isDefault Is default events request from data provider or not
     */
    Retriever.prototype.setActiveRequest = function (type, isDefault) {
        if (isDefault === void 0) { isDefault = true; }
        var defaultRequest = (type === ActiveRequestsType.fixtures
            ? __assign({}, this.dataProvider.eventsRequest) : __assign({}, this.dataProvider.eventsRequestOutrights));
        this.activeRequests[type] = isDefault ? defaultRequest : {
            request: {
                eventTypes: defaultRequest && defaultRequest.request ? defaultRequest.request.eventTypes : []
            },
            type: EventsRequestType.ByEventIds
        };
    };
    /**
     * Get query params for query to SportData API
     */
    Retriever.prototype.getQueryParams = function (type) {
        return {
            request: this.activeRequests[type].request,
            messageHandler: this.regularUpdateTimeOut,
        };
    };
    Retriever.prototype.regularUpdateTimeOut = function (x) {
        setTimeout(this.regularUpdate.bind(this), 0, x);
    };
    /**
     * Get BFF request params for query to BFF
     */
    Retriever.prototype.getBFFRequestParams = function (type) {
        return {
            request: this.activeRequests[type].request,
            cardConfiguration: this.dataProvider.isMiniEventCard ? this.dataProvider.cardConfigurationEventView
                : this.dataProvider.cardConfiguration,
            allSportIds: this.dataProvider.allSportIds
        };
    };
    Retriever.prototype.initializeRequests = function () {
        var requests = {};
        if (this.dataProvider.eventsRequest) {
            requests.fixtures = __assign({}, this.dataProvider.eventsRequest);
        }
        if (this.dataProvider.eventsRequestOutrights) {
            requests.outrights = __assign({}, this.dataProvider.eventsRequestOutrights);
        }
        return requests;
    };
    Retriever.prototype.initializeLoaders = function () {
        var fixturesloader = this.dataProvider.eventsRequest
            ? { isLoading: true, isFirstLoad: true, isFullLoadFinished: false }
            : { isLoading: false, isFirstLoad: true, isFullLoadFinished: true };
        var outrightsloader = this.dataProvider.eventsRequestOutrights
            ? { isLoading: true, isFirstLoad: true, isFullLoadFinished: false }
            : { isLoading: false, isFirstLoad: true, isFullLoadFinished: true };
        return {
            fixtures: fixturesloader,
            outrights: outrightsloader
        };
    };
    Retriever.prototype.isRequestAvailable = function () {
        return (this.activeRequests.fixtures && this.isIdsAvailable(this.activeRequests.fixtures.request)) ||
            (this.activeRequests.outrights && this.isIdsAvailable(this.activeRequests.outrights.request));
    };
    Retriever.prototype.isIdsAvailable = function (request) {
        return (request.ids && request.ids.length) || request.sportId;
    };
    Object.defineProperty(Retriever.prototype, "loader", {
        get: function () {
            return {
                isLoading: this.subLoaders.fixtures.isLoading || this.subLoaders.outrights.isLoading,
                isFirstLoad: this.subLoaders.fixtures.isFirstLoad && this.subLoaders.outrights.isFirstLoad,
                isFullLoadFinished: this.subLoaders.fixtures.isFullLoadFinished && this.subLoaders.outrights.isFullLoadFinished
            };
        },
        enumerable: true,
        configurable: true
    });
    Retriever.prototype.buildUpdateQuery = function (request, response, requestType) {
        // Not subscribing for SportData API updates when outright request
        requestType === ActiveRequestsType.outrights
            ? this.updateRetrieverData(request, response, requestType)
            : this["buildUpdateQuery" + this.activeRequests[requestType].type](response, requestType);
    };
    Retriever.prototype.updateRetrieverData = function (request, response, requestType) {
        var filterFn = this.getFilterFunction(request, requestType);
        this.subLoaders[requestType].isFirstLoad ? this.updateCurrentData(response, filterFn, requestType) : this.updateTemporaryData(response);
    };
    Retriever.prototype.getFilterIds = function (request, requestType) {
        if (this.activeRequests[requestType].type === EventsRequestType.ByLeagueIds) {
            return this.currentSports;
        }
        if (this.activeRequests[requestType].type === EventsRequestType.ByRegionId) {
            return [request.sportId];
        }
        return request.ids;
    };
    Retriever.prototype.isValidResponse = function (activeRequest, response, requestType) {
        var isCurrentRequest = activeRequest.ids ? !compareArrays(activeRequest.ids, this.activeRequests[requestType].request.ids) : false;
        return isCurrentRequest || isDifferentResponse(activeRequest, response);
    };
    __decorate([
        observable,
        __metadata("design:type", Object)
    ], Retriever.prototype, "data", void 0);
    __decorate([
        observable,
        __metadata("design:type", Object)
    ], Retriever.prototype, "subLoaders", void 0);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "activate", null);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "deactivate", null);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "querySportUpdate", null);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, Array]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "queryDatesUpdate", null);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array, Array]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "queryLeaguesUpdate", null);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String, String]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "queryRegionIdUpdate", null);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Number]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "queryTagsUpdate", null);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Array]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "queryFavoritesUpdate", null);
    __decorate([
        Debounce(200),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "applyChanges", null);
    __decorate([
        action.bound,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "replaceLeagues", null);
    __decorate([
        action.bound,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, Function, String]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "updateCurrentData", null);
    __decorate([
        Debounce(500),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "applyAdd", null);
    __decorate([
        action.bound,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "batchedAdd", null);
    __decorate([
        action.bound,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "remove", null);
    __decorate([
        action.bound,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "batchedChange", null);
    __decorate([
        action.bound,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Boolean, String]),
        __metadata("design:returntype", void 0)
    ], Retriever.prototype, "setLoader", null);
    __decorate([
        computed,
        __metadata("design:type", Object),
        __metadata("design:paramtypes", [])
    ], Retriever.prototype, "loader", null);
    return Retriever;
}());
export { Retriever };
//# sourceMappingURL=retriever.js.map